/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import TemplateForm from "components/forms/TemplateForm";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { TableProps } from "interfaces";
import { HeaderEnum, TemplateInterface, TemplateTypeEnum } from "medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewTemplateDataContent } from "utils/model.utils";
import Box from "components/generals/Box";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ButtonDialog from "components/generals/ButtonDialog";
import PermissionWrapper from "components/generals/PermissionWrapper";

/**
 * Template table component for displaying and managing clinical templates.
 * 
 * Features:
 * - Displays template information in sortable data table
 * - Columns: title, type, version, tags, action buttons
 * - View/edit template functionality via modal with TemplateForm
 * - Delete template with confirmation dialog and list update
 * - Optional select function for template selection workflow
 * - Read-only mode support
 * - Tags display with array-to-string conversion
 * - Permission-based access control
 * - Version tracking for templates
 * 
 * Template Types:
 * - Clinical note templates
 * - Prescription templates
 * - HPI (History of Present Illness) templates
 * - Assessment templates
 * - Treatment plan templates
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before operations
 * 
 * @param {TableProps<TemplateInterface>} props - Table properties
 * @param {TemplateInterface[]} props.modelList - Array of template records
 * @param {Function} props.setModelList - Callback to update template list
 * @param {Function} [props.selectFunction] - Optional callback for template selection
 * @param {boolean} [props.readOnlyValue] - Whether table is in read-only mode
 * 
 * @returns {JSX.Element} Template data table with version tracking
 */
interface TemplateTableProps extends TableProps<TemplateInterface> {
  onOpenRecycleBin?: () => void;
}

const TemplateTable: FC<TemplateTableProps> = ({
  modelList,
  setModelList,
  selectFunction,
  readOnlyValue,
  onOpenRecycleBin,
  ...props
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateInterface>(
    getNewTemplateDataContent()
  );
  const [activeEditTemplateModal, setActiveEditTemplateModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();

  const getUITable = (list: TemplateInterface[]) => {
    return {
      columns: [
        {
          Header: t("prescriptiontemplateTable.title"),
          accessor: "title",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("prescriptiontemplateTable.type"),
          accessor: "type",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={t(`prescriptiontemplateTable.${value}`)} />,
        },
        {
          Header: t("prescriptiontemplateTable.version"),
          accessor: "version",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("prescriptiontemplateTable.tags"),
          accessor: "tags",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => {
            const displayValue = Array.isArray(value) ? value.join(", ") : value;
            return <DefaultCell value={displayValue} />;
          },
        },
        {
          Header: t("prescriptiontemplateTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewTemplateButton"
                sx={{ marginRight: 2 }}
                variant="contained"
                color="info"
                onClick={() => {
                  const template = list.find((p) => p.id === value);
                  if (template) {
                    setModalAction(FormActionType.MODIFY);
                    openModalHandler(template);
                  }
                }}
              >
                {t("prescriptiontemplateTable.view")}
              </Button>
              <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.DOCTORS].DELETE]}
                >
                  <ButtonDialog
                    name="deleteTemplateButton"
                    onAcceptHandle={() => {
                      const template = list.find((p) => p.id === value);
                      if (template) {
                        deleteHandler(template);
                      }
                    }}
                    buttonText={t("prescriptiontemplateTable.deleteAction")}
                    title={t("prescriptiontemplateTable.deleteConfirmation")}
                    description={t("prescriptiontemplateTable.description")}
                    labelledbyID={"TemplateDelete"}
                    describedbyID={"TemplateDescriptionId"}
                  />
                </PermissionWrapper>
            </>
          ),
        },
      ],

      rows: list,
    };
  };
  const dataTableData = useMemo(() => getUITable(modelList), [modelList]);
  const [_isLoading, _error, sendRequest] = useHttp();

  const openModalHandler = (template: TemplateInterface) => {
    setSelectedTemplate(template);
    setActiveEditTemplateModal(true);
  };

  const deleteHandler = (template: TemplateInterface) => {
    setSelectedTemplate(template);
    sendRequest<boolean, TemplateInterface>(
      CoreAPI_URLs.HPI_TEMPLATE_GENERAL_OPERATIONS,
      onTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(template),
      [{ header: HeaderEnum.RESOURCE_ID, value: template.id }],
      { payload: template, successMessage: t("templateForm.templateDeleted") }
    );
  };

  const onTemplateDeleteHandler = (result: boolean, template: TemplateInterface) => {
    const indexList = modelList.indexOf(template);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
        <PermissionWrapper requiredPermissions={[
          PermissionsUser[EntityPermissions.HPI_PAGE].CREATE,
          PermissionsUser[EntityPermissions.HPI_PAGE].EDIT,
          PermissionsUser[EntityPermissions.HPI_PAGE].DELETE
        ]}>
          <Button
            name="recycleBinButton"
            variant="contained"
            color="secondary"
            startIcon={<DeleteOutlineIcon sx={{ color: '#fff' }} />}
            onClick={onOpenRecycleBin}
          >
            {t("templatesDataContent.recycleBin")}
          </Button>
        </PermissionWrapper>
        <PermissionWrapper requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].VIEW]}>
          <Button
            name="newTemplateButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedTemplate(getNewTemplateDataContent);
              setActiveEditTemplateModal(true);
            }}
          >
            {t("templatesDataContent.newTemplate")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading} />
      <Modal
        open={activeEditTemplateModal}
        onClose={() => {
          setActiveEditTemplateModal(false);
        }}
      >
        <TemplateForm
          readOnlyValue={readOnlyValue}
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedTemplate}
          onCloseAction={() => {
            setActiveEditTemplateModal(false);
          }}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default TemplateTable;
